import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { getTopRankings, getMainScreenSettings } from '../lib/api/ranker-api';
import { getSidoList } from '../lib/api/bjd-api';
import { MAIN_CATEGORY_MAP, buildDefaultMainScreenConfig, mergeSavedIntoConfig } from '../lib/config/main-screen-config';
import './MainTopRankings.css';

// ─────────────────────────────────────────────────────────────────────────────
// 값 포맷터
const fmtEok = (manwon) => {
  const n = Number(manwon);
  if (!Number.isFinite(n) || n === 0) return '-';
  return `${Math.round((n / 10000) * 100) / 100}억`;
};
const fmtPct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
};
const fmtRate = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '-';
  return `${n.toFixed(1)}%`;
};
const asArray = (res) => (Array.isArray(res) ? res : (res?.data ?? res?.items ?? []));

// 타입 문자열 → 평 라벨 (첫 숫자 기준, >60 이면 ㎡→평)
const parsePyeongLabel = (str) => {
  const m = String(str || '').match(/\d+(?:\.\d+)?/);
  if (!m) return '';
  let n = Number(m[0]);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n > 60) n /= 3.305785;
  return `${Math.round(n)}평`;
};

const REGION_SHORT = {
  '서울특별시': '서울', '부산광역시': '부산', '대구광역시': '대구', '인천광역시': '인천',
  '광주광역시': '광주', '대전광역시': '대전', '울산광역시': '울산', '세종특별자치시': '세종',
  '경기도': '경기', '강원특별자치도': '강원', '강원도': '강원', '충청북도': '충북', '충청남도': '충남',
  '전북특별자치도': '전북', '전라북도': '전북', '전라남도': '전남', '경상북도': '경북', '경상남도': '경남',
  '제주특별자치도': '제주',
};
const shortRegion = (name) => REGION_SHORT[String(name || '').trim()] || String(name || '').trim();

// (지역, 예산) → 랭킹응답 캐시
const rankingCache = new Map();
const fetchRankings = (bjdCode, investMax) => {
  const region = bjdCode || '11';
  const key = `${region}|${investMax || ''}`;
  if (!rankingCache.has(key)) {
    rankingCache.set(key, getTopRankings({ bjdCode: region, limit: 10, investMax }).catch((e) => {
      rankingCache.delete(key);
      throw e;
    }));
  }
  return rankingCache.get(key);
};

const NATION_REGION = { code: 'ALL', name: '전국' };
const DEFAULT_REGIONS = [NATION_REGION, { code: '11', name: '서울' }];
const DEFAULT_BUDGET = 100000; // 10억(만원)

// 기간(주간/월간/반년/1년) — 랭킹 응답 키 접미사(riseWeekly 등)
const PERIODS = [
  { key: 'weekly', label: '주간', suffix: 'Weekly' },
  { key: 'monthly', label: '월간', suffix: 'Monthly' },
  { key: 'halfyear', label: '반년', suffix: 'Halfyear' },
  { key: 'year', label: '1년', suffix: 'Yearly' },
];
const periodSuffix = (key) => (PERIODS.find((p) => p.key === key)?.suffix || 'Weekly');

// 급지(시/동) — 99/0/빈값은 '-'
const gradeLabel = (it) => {
  const has = (v) => v != null && v !== '' && String(v) !== '99' && Number(v) !== 0;
  const s = it?.sigunguGrade;
  const d = it?.dongGrade;
  if (!has(s) && !has(d)) return '-';
  return `${has(s) ? s : '-'} / ${has(d) ? d : '-'}`;
};
const fmtCount = (v, unit) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '-';
  return `${n.toLocaleString('ko-KR')}${unit}`;
};

// 백엔드 연동 전(값 미제공) 표기
const Pending = () => <span className="mtr-pending">준비중</span>;

// ─────────────────────────────────────────────────────────────────────────────
// 인라인 드롭다운 (지역/기간 공용 스타일)
function InlineDropdown({ value, options, onChange, bracket = false, title }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const current = options.find((o) => o.code === value);
  const label = current?.name || '선택';
  return (
    <span className="mtr-region-dd" ref={ref}>
      <button type="button" className="mtr-region-dd-btn" onClick={() => setOpen((o) => !o)} title={title || '선택'}>
        <span className="mtr-region-dd-name">{bracket ? `[${label}]` : label}</span>
        <span className="mtr-region-dd-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="mtr-region-dd-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.code}
              className={`mtr-region-dd-item ${o.code === value ? 'active' : ''}`}
              onClick={() => { if (o.code !== value) onChange(o.code); setOpen(false); }}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 표 컬럼 정의. render(it, cat) → 셀 내용. 값 미제공 컬럼은 <Pending/>.
const regionText = (it) => [shortRegion(it?.sidoName), it?.sigunguName, it?.bjdName].filter(Boolean).join(' ') || '-';

const COLUMN_DEFS = {
  rank: { label: '#', cls: 'mtr-t-rank', render: (it) => it.rank },
  name: {
    label: '단지명',
    cls: 'mtr-t-name',
    render: (it, cat) => {
      const py = parsePyeongLabel(it.planTypeName);
      // 단지명 클릭 시 네이버 부동산으로 이동
      const openNaver = (e) => {
        e.stopPropagation();
        if (it.naverDanjiCode) window.open(`https://new.land.naver.com/complexes/${it.naverDanjiCode}`, '_blank', 'noopener,noreferrer');
      };
      return (
        <span className="mtr-t-name-wrap">
          <button
            type="button"
            className="mtr-t-name-text mtr-t-name-btn"
            title={`${it.buildingName || ''}${py ? ` (${py})` : ''} — 네이버로 이동`}
            onClick={openNaver}
          >
            {it.buildingName || '-'}{py ? <span className="mtr-t-py"> ({py})</span> : null}
          </button>
        </span>
      );
    },
  },
  region: { label: '지역/생활권', cls: 'mtr-t-region', render: (it) => regionText(it) },
  grade: { label: '급지', cls: 'mtr-t-num', render: (it) => gradeLabel(it) },
  rateOwn: { label: '값', cls: 'mtr-t-num', render: (it, cat) => (<span style={{ color: cat.color, fontWeight: 800 }}>{cat.metricFmt === 'rate' ? fmtRate(it.value) : fmtPct(it.value)}</span>) },
  tradePrice: { label: '매매가', cls: 'mtr-t-num', render: (it) => fmtEok(it.articleTradePrice) },
  tradeReal: { label: '매매실거래가', cls: 'mtr-t-num', render: (it) => fmtEok(it.avgSaleRealPrice) },
  prevTrade: { label: '이전 매매가', cls: 'mtr-t-num', render: (it) => fmtEok(it.refBase) },
  leasePrice: { label: '전세가', cls: 'mtr-t-num', render: (it) => fmtEok(it.articleLeasePrice) },
  leaseReal: { label: '전세실거래가', cls: 'mtr-t-num', render: (it) => fmtEok(it.avgLeaseRealPrice) },
  invest: { label: '투자금', cls: 'mtr-t-num', render: (it) => fmtEok(it.investPrice) },
  articleCount: { label: '매물수', cls: 'mtr-t-num', render: (it) => fmtCount(it.tradeArticleCount, '개') },
  realCount: { label: '실거래건수', cls: 'mtr-t-num', render: (it) => fmtCount(it.realTxCount, '건') },
  topPrice: { label: '최고가', cls: 'mtr-t-num', render: (it) => fmtEok(it.topPrice) },
  lowPrice: { label: '최저가', cls: 'mtr-t-num', render: (it) => fmtEok(it.lowPrice) },
  properHigh: { label: '적고가', cls: 'mtr-t-num', render: (it) => fmtEok(it.properHighPrice) },
  leaseRate: { label: '전세가율', cls: 'mtr-t-num', render: (it) => fmtRate(it.leasePriceRate) },
  txRatio: { label: '거래비율', cls: 'mtr-t-num', render: () => <Pending /> },
};

// 카테고리 메타/컬럼 정의는 공용 설정 모듈(main-screen-config)의 MAIN_CATEGORIES 사용.
// 관리자 '메인화면설정'에서 표시/순서/형태(표·차트)를 제어한다.

// 투자금 예산 옵션(억 단위 세부). code=만원 문자열
const DEFAULT_BUDGET_CODE = String(DEFAULT_BUDGET);
const BUDGET_OPTIONS = [1, 2, 3, 4, 5, 7, 10, 15, 20, 30, 50]
  .map((eok) => ({ code: String(eok * 10000), name: `${eok}억` }));

// ─────────────────────────────────────────────────────────────────────────────
// 랭킹 카드: 한 줄에 표(좌) + 차트(우) — 같은 데이터. showChart=false 면 표만.
function RankingCard({ cat, regions, showChart = true }) {
  const [regionCode, setRegionCode] = useState('11');
  const [period, setPeriod] = useState('weekly');
  const [budget, setBudget] = useState(DEFAULT_BUDGET_CODE);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const rankKey = cat.rankKeyBase ? `${cat.rankKeyBase}${periodSuffix(period)}` : cat.rankKey;
  const investMax = cat.hasBudget ? Number(budget) : DEFAULT_BUDGET;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchRankings(regionCode, investMax)
      .then((res) => { if (alive) { setItems(res?.[rankKey] || []); setLoading(false); } })
      .catch(() => { if (alive) { setItems([]); setLoading(false); } });
    return () => { alive = false; };
  }, [regionCode, rankKey, investMax]);

  const option = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const rows = [...list].reverse();
    const labels = rows.map((it) => {
      const py = parsePyeongLabel(it.planTypeName);
      return py ? `${it.buildingName || '-'} (${py})` : (it.buildingName || '-');
    });
    const values = rows.map((it) => {
      if (cat.valueKind === 'invest') return Number(it.investPrice ?? it.value) / 10000;
      if (cat.valueKind === 'price') return Number(it.articleTradePrice ?? it.value) / 10000;
      if (cat.valueKind === 'rate') return Number(it.value) || 0;
      return Number(it.txCount ?? it.value) || 0;
    });
    const unit = (cat.valueKind === 'rate') ? 'pct' : (cat.valueKind === 'count' ? 'cnt' : 'eok');
    const fmtVal = (v) => (unit === 'eok' ? `${Math.round(v * 100) / 100}억` : unit === 'pct' ? `${Number(v).toFixed(2)}%` : `${v}`);
    return {
      animation: false,
      grid: { left: 6, right: 72, top: 6, bottom: 6, containLabel: true },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: 'rgba(255,255,255,0.97)', borderColor: '#e2e8f0', borderWidth: 1, textStyle: { color: '#0f172a', fontSize: 13 } },
      xAxis: { type: 'value', axisLabel: { fontSize: 12, color: '#475569', formatter: fmtVal }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
      yAxis: { type: 'category', data: labels, axisTick: { show: false }, axisLine: { show: false }, axisLabel: { fontSize: 13, color: '#1f2937', fontWeight: 600 } },
      series: [{
        type: 'bar', data: values, barMaxWidth: 18,
        itemStyle: { color: cat.color, borderRadius: [0, 3, 3, 0] },
        label: { show: true, position: 'right', fontSize: 13, fontWeight: 700, color: '#1f2937', formatter: (p) => fmtVal(p.value) },
      }],
    };
  }, [items, cat]);

  const list = Array.isArray(items) ? items : [];
  const cols = cat.columns.map((id) => {
    const def = COLUMN_DEFS[id];
    const label = id === 'rateOwn' ? (cat.metricLabel || '값') : def.label;
    return { id, label, cls: def.cls, render: def.render };
  });
  const hasData = list.length > 0;

  return (
    <div className="mtr-card">
      <div className="mtr-card-head">
        <div className="mtr-card-title">
          <InlineDropdown value={regionCode} options={regions} onChange={setRegionCode} title="지역 선택" />
          {cat.hasPeriod && (
            <InlineDropdown value={period} options={PERIODS.map((p) => ({ code: p.key, name: p.label }))} onChange={setPeriod} bracket title="기간 선택" />
          )}
          {cat.hasBudget && (
            <InlineDropdown value={budget} options={BUDGET_OPTIONS} onChange={setBudget} bracket title="투자금 선택" />
          )}
          <span className="mtr-card-title-text">{cat.title} 순위 TOP 10</span>
        </div>
      </div>
      {hasData ? (
        showChart ? (
          <div className="mtr-card-row">
            <div className="mtr-rank-table-wrap mtr-card-table">
              <table className="mtr-rank-table">
                <thead>
                  <tr>{cols.map((c) => <th key={c.id} className={c.cls}>{c.label}</th>)}</tr>
                </thead>
                <tbody>
                  {list.map((it) => (
                    <tr key={`${it.rank}-${it.naverDanjiCode}`}>
                      {cols.map((c) => <td key={c.id} className={c.cls}>{c.render(it, cat)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mtr-card-chart">
              <ReactECharts option={option} style={{ width: '100%', height: '100%' }} opts={{ renderer: 'canvas' }} notMerge />
            </div>
          </div>
        ) : (
          <div className="mtr-rank-table-wrap">
            <table className="mtr-rank-table">
              <thead>
                <tr>{cols.map((c) => <th key={c.id} className={c.cls}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {list.map((it) => (
                  <tr key={`${it.rank}-${it.naverDanjiCode}`}>
                    {cols.map((c) => <td key={c.id} className={c.cls}>{c.render(it, cat)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="mtr-card-empty">{loading ? '불러오는 중...' : '데이터 준비중 (백엔드 연동 예정)'}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function MainTopRankings() {
  const [regions, setRegions] = useState(DEFAULT_REGIONS);
  // 관리자 '메인화면설정'(DB)에서 표시/순서/형태 로드. 실패 시 기본값.
  const [config, setConfig] = useState(() => buildDefaultMainScreenConfig());

  useEffect(() => {
    let alive = true;
    getMainScreenSettings()
      .then((res) => {
        const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
        if (alive) setConfig(mergeSavedIntoConfig(items));
      })
      .catch(() => { /* 실패 시 기본값 유지 */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getSidoList();
        const list = asArray(res)
          .map((r) => {
            const raw = String(r?.bjdCode || r?.dongCode || r?.code || '').trim();
            const code = raw.slice(0, 2);
            const name = shortRegion(r?.sidoName || r?.bjdName || r?.name || '');
            return code && name ? { code, name } : null;
          })
          .filter(Boolean)
          .filter((v, i, arr) => arr.findIndex((x) => x.code === v.code) === i);
        if (alive && list.length > 0) setRegions([NATION_REGION, ...list]);
      } catch (e) { /* 실패 시 기본(서울) 유지 */ }
    })();
    return () => { alive = false; };
  }, []);

  // 관리자 '메인화면설정'(DB)의 표시/순서/형태를 반영.
  const visible = config
    .filter((c) => c.enabled)
    .map((c) => ({ ...c, cat: MAIN_CATEGORY_MAP[c.id] }))
    .filter((c) => c.cat);

  return (
    <section className="mtr-root">
      <h2 className="mtr-heading">지역별 TOP 10 랭킹</h2>

      <div className="mtr-grid mtr-grid-single">
        {visible.map(({ id, type, cat }) => (
          <RankingCard key={id} cat={cat} regions={regions} showChart={type !== 'table'} />
        ))}
      </div>
    </section>
  );
}

export default MainTopRankings;
